/**
 * Email sync hooks — TanStack Query wrappers around the
 * ``emailManagement`` adapter + the unified ``SyncStore``.
 *
 * Public surface (used by ``EmailSyncFiltersSheet``, ``$capability``,
 * ``ConnectedAccountRow``):
 *
 *   - ``useEmailLabels``        — pre-sync label picker data
 *   - ``useEmailSyncStatus``    — single-account status query (5s poll)
 *   - ``useEmailSyncTrigger``   — mutation: POST sync-with-filters
 *   - ``useEmailForceSync``     — mutation: POST start-sync (sync_type=full)
 *   - ``useEmailSyncCancel``    — mutation: POST cancel-sync
 *   - ``useEmailPreviewSync``   — debounced preview lookup
 *   - ``useEmailPresets``       — server-persisted filter snapshots
 *   - ``useSaveEmailPreset`` / ``useDeleteEmailPreset``
 */

import {
  cancelEmailSync,
  deleteEmailPreset,
  type EmailFilterPreset,
  type EmailLabel,
  type EmailPreviewSyncResponse,
  type EmailProvider,
  type EmailSyncFilters,
  type EmailSyncStatus,
  getEmailLabels,
  getEmailSyncStatus,
  listEmailPresets,
  previewEmailSync,
  saveEmailPreset,
  startEmailSync,
  syncEmailWithFilters,
} from "@ando/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useChatUserId } from "@/features/chat/hooks/useChatSession";
import { useSyncStore } from "@/features/sync/state/syncStore";

// ──────────────────────────────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────────────────────────────

const emailQueryKeys = {
  labels: (userId: string, emailAddress: string, provider: EmailProvider) =>
    ["emailManagement", "labels", userId, emailAddress, provider] as const,
  status: (userId: string, emailAddress: string, provider: EmailProvider) =>
    ["emailManagement", "status", userId, emailAddress, provider] as const,
  presets: (userId: string, emailAddress: string) =>
    ["emailManagement", "presets", userId, emailAddress] as const,
} as const;

// ──────────────────────────────────────────────────────────────────────
// Read hooks
// ──────────────────────────────────────────────────────────────────────

const LABELS_STALE_MS = 5 * 60_000;
const STATUS_POLL_MS = 5_000;

export function useEmailLabels(
  emailAddress: string | null | undefined,
  provider: EmailProvider,
) {
  const userId = useChatUserId();
  return useQuery({
    queryKey: emailQueryKeys.labels(userId ?? "", emailAddress ?? "", provider),
    queryFn: () =>
      getEmailLabels(userId as string, emailAddress as string, provider),
    enabled: !!userId && !!emailAddress,
    staleTime: LABELS_STALE_MS,
  });
}

export function useEmailSyncStatus(
  emailAddress: string | null | undefined,
  provider: EmailProvider,
): EmailSyncStatus | null {
  const userId = useChatUserId();
  const { data } = useQuery({
    queryKey: emailQueryKeys.status(
      userId ?? "",
      emailAddress ?? "",
      provider,
    ),
    queryFn: () =>
      getEmailSyncStatus(userId as string, emailAddress as string, provider),
    enabled: !!userId && !!emailAddress,
    // Poll while the connection is being actively monitored — the
    // ``SyncStore`` SSE feed is the primary truth source; this poll just
    // keeps the resumable_job_* fields fresh for the resume banner.
    refetchInterval: STATUS_POLL_MS,
  });
  return data ?? null;
}

export function useEmailPresets(
  emailAddress: string | null | undefined,
) {
  const userId = useChatUserId();
  return useQuery({
    queryKey: emailQueryKeys.presets(userId ?? "", emailAddress ?? ""),
    queryFn: () =>
      listEmailPresets(userId as string, emailAddress as string),
    enabled: !!userId && !!emailAddress,
    staleTime: 60_000,
  });
}

// ──────────────────────────────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────────────────────────────

export interface EmailSyncTriggerVars {
  readonly emailAddress: string;
  readonly provider: EmailProvider;
  readonly filters: EmailSyncFilters;
  readonly maxMessages?: number;
}

/**
 * Mutation — POST ``/email-management/sync-with-filters`` + register the
 * resulting job with the unified ``SyncStore`` so the universal toast +
 * per-row status pill light up automatically.
 */
export function useEmailSyncTrigger() {
  const userId = useChatUserId();
  const trackJob = useSyncStore((s) => s.trackJob);
  return useMutation({
    mutationFn: async (vars: EmailSyncTriggerVars) => {
      if (!userId) throw new Error("Sessione utente assente");
      const res = await syncEmailWithFilters({
        user_id: userId,
        email_address: vars.emailAddress,
        provider: vars.provider,
        filters: vars.filters,
        max_messages: vars.maxMessages,
      });
      if (res.job_id) {
        trackJob(res.job_id, "email", {
          user_id: userId,
          provider: vars.provider,
          email_address: vars.emailAddress,
        });
      }
      return res;
    },
  });
}

export interface EmailForceSyncVars {
  readonly emailAddress: string;
  readonly provider: EmailProvider;
  /** ``auto`` = incremental resume; ``full`` = re-index everything. */
  readonly syncType: "auto" | "full";
  readonly forceNew?: boolean;
}

/** Mutation — POST ``/email-management/start-sync`` (auto/full). */
export function useEmailForceSync() {
  const userId = useChatUserId();
  const trackJob = useSyncStore((s) => s.trackJob);
  return useMutation({
    mutationFn: async (vars: EmailForceSyncVars) => {
      if (!userId) throw new Error("Sessione utente assente");
      const res = await startEmailSync({
        user_id: userId,
        email_address: vars.emailAddress,
        provider: vars.provider,
        sync_type: vars.syncType,
        force_new: vars.forceNew ?? false,
      });
      if (res.job_id) {
        trackJob(res.job_id, "email", {
          user_id: userId,
          provider: vars.provider,
          email_address: vars.emailAddress,
        });
      }
      return res;
    },
  });
}

/**
 * Mutation — cancel an in-flight email sync job.
 *
 * Delegates the optimistic flip to the SyncStore so the universal toast
 * shows the cancelling spinner immediately, without waiting on the BE
 * to acknowledge the cancel request.
 */
export function useEmailSyncCancel() {
  const userId = useChatUserId();
  const cancelInStore = useSyncStore((s) => s.cancelJob);
  return useMutation({
    mutationFn: async (jobId: string) => {
      if (!userId) throw new Error("Sessione utente assente");
      // Optimistic flip first; the BE call follows in parallel.
      void cancelInStore(jobId);
      return cancelEmailSync(jobId, userId);
    },
  });
}

export interface EmailPreviewVars {
  readonly emailAddress: string;
  readonly provider: EmailProvider;
  readonly filters: EmailSyncFilters;
}

/**
 * Mutation — POST ``/email-management/preview-sync``. Returns the count
 * + ETA so the FE can disable the Sync button when the count is zero.
 *
 * Implemented as a mutation (not query) because the FE typically
 * debounces filter changes to ~400ms before previewing — using a query
 * here would mean either staleness or thrash.
 */
export function useEmailPreviewSync() {
  const userId = useChatUserId();
  return useMutation({
    mutationFn: async (vars: EmailPreviewVars): Promise<EmailPreviewSyncResponse> => {
      if (!userId) throw new Error("Sessione utente assente");
      return previewEmailSync({
        user_id: userId,
        email_address: vars.emailAddress,
        provider: vars.provider,
        filters: vars.filters,
      });
    },
  });
}

export interface SaveEmailPresetVars {
  readonly emailAddress: string;
  readonly presetName: string;
  readonly filters: EmailSyncFilters;
  readonly isDefault?: boolean;
}

export function useSaveEmailPreset() {
  const userId = useChatUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: SaveEmailPresetVars): Promise<EmailFilterPreset> => {
      if (!userId) throw new Error("Sessione utente assente");
      return saveEmailPreset({
        user_id: userId,
        email_address: vars.emailAddress,
        preset_name: vars.presetName,
        filters: vars.filters,
        is_default: vars.isDefault,
      });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: emailQueryKeys.presets(userId ?? "", vars.emailAddress),
      });
    },
  });
}

export function useDeleteEmailPreset(emailAddress: string) {
  const userId = useChatUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (presetId: string): Promise<void> => {
      await deleteEmailPreset(presetId);
    },
    onSuccess: () => {
      if (!userId) return;
      void qc.invalidateQueries({
        queryKey: emailQueryKeys.presets(userId, emailAddress),
      });
    },
  });
}

// Re-export the underlying types so callers don't need a second import.
export type {
  EmailFilterPreset,
  EmailLabel,
  EmailProvider,
  EmailSyncFilters,
  EmailSyncStatus,
  EmailPreviewSyncResponse,
};
